import { useState, useEffect, useRef } from "react";
import { getVisits } from "../../../service/Visits.service";
import NeoRangeNumberFilter from "../../../design/design_components/neo/table/NeoRangeNumberFilter.base";
import NeoDateRangeFilter from "../../../design/design_components/neo/table/NeoDateRangeFilter.base";
import NeoTable from "../../../design/design_components/neo/table/NeoTable.base";
import NeoTableColumn from "../../../design/design_components/neo/table/NeoTableColumn.base";
import Routes from "../../../routers/Routes.router.json";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import usePrevious from "hooks/usePrevious.hook";

export default function ContactsTable({ columnsSelected }) {
  const columnsSelectedPrevious = usePrevious(columnsSelected);
  const ROWS_COUNT = 10;
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [data, setData] = useState([]);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: ROWS_COUNT,
    page: 1,
    sortField: "created_at",
    sortOrder: -1,
    filters: {
      "created_at": {
        value: [
          new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()),
          new Date()
        ],
        matchMode: "range"
      }
    }
  });
  const lazyParamsPrevious = usePrevious(lazyParams);

  const tableRef = useRef(null);
  const history = useHistory(null);
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    // Evita ejecutar la consulta al backend al menos que se esté mostrando la columna Duración
    // Esto es para evitar que se haga la consulta cada vez que se selecciona o deselecciona una columna
    // ya que la columna Duración es la única que requiere un cálculo adicional en el backend
    if (columnsSelectedPrevious !== undefined && lazyParamsPrevious !== undefined) {
      if (lazyParams === lazyParamsPrevious) {
        let stop = true;
        if (columnsSelected.includes("Duración") && !columnsSelectedPrevious.includes("Duración")) {
          stop = false;
        }
        if(stop) return;
      }
    }

    setLoading(true);
    getVisits(lazyParams, columnsSelected).then((visitsData) => {
      if (visitsData.success && isMounted.current) {
        setData(visitsData.payload.visits);
        setTotalRecords(visitsData.payload.totalRecords);
      }
    }).finally(() => {
      setLoading(false);
    });
  }, [lazyParams, columnsSelected]);

  const goToVisitDetail = (visitData) => {
    const { Visit } = visitData;
    const { uuid } = Visit;
    history.push(`${Routes.visits.detailsWithOutParams}/${uuid}`, visitData);
  };

  return (
    <div>
      <div className="content-section implementation">
        <div className="card">
          <NeoTable
            emptyMessage={"No hay visitas registradas"}
            ref={tableRef}
            value={data}
            lazy
            selectionMode="single"
            dataKey="Visit.uuid"
            autoLayout={true}
            paginator
            first={lazyParams.first}
            rows={ROWS_COUNT}
            totalRecords={totalRecords}
            removableSort
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            onPage={(event) => setLazyParams((lazyParams) => { return { ...lazyParams, ...event } })}
            onSort={(event) => setLazyParams((lazyParams) => { return { ...lazyParams, ...event } })}
            onFilter={(event) => setLazyParams((lazyParams) => { return { ...lazyParams, ...event } })}
            filters={lazyParams.filters}
            loading={loading}
            onRowSelect={(e) => goToVisitDetail(e.data)}
            filterDelay={1000}
          >
            <NeoTableColumn
              header="Fecha"
              field="created_at"
              filter
              filterField="created_at"
              filterElement={
                <NeoDateRangeFilter
                  ref={tableRef}
                  field={"created_at"}
                  placeholder={"Rango de fechas"}
                  matchFilter="range"
                  value={[lazyParams.filters.created_at.value[0], lazyParams.filters.created_at.value[1]]}
                  showButtonBar={false}
                />
              }
              filterMatchMode="range"
              dataType="date"
              body={(row) => {
                const { created_at } = row;
                if (Boolean(created_at) === false) return null;
                return Intl.DateTimeFormat(undefined, {
                  month: "long",
                  hour12: false,
                  hour: "2-digit",
                  minute: "2-digit",
                  year: "numeric",
                  day: "numeric",
                }).format(moment(created_at).toDate())
              }}
              sortable
            />
            <NeoTableColumn
              header="Contacto"
              field="Contact.email"
              filter
              filterMatchMode="contains"
              filterPlaceholder="Buscar por correo"
              sortable
            />
            <NeoTableColumn
              header="Sucursal"
              field="Ap.Store.name"
              filter
              filterMatchMode="contains"
              filterPlaceholder="Buscar por sucursal"
              sortable
            />
            {columnsSelected.includes("Dispositivo") &&
              <NeoTableColumn
                header="Dispositivo"
                field="Device.os"
                filter
                filterMatchMode="contains"
                filterPlaceholder="Buscar por dispositivo"
                sortable
              />
            }
            {columnsSelected.includes("Mac address") &&
              <NeoTableColumn
                header="Mac Address"
                field="Device.mac_address"
                filter
                filterMatchMode="contains"
                filterPlaceholder="Buscar por mac address"
                sortable
              />
            }
            {columnsSelected.includes("Duración") &&
              <NeoTableColumn
                header="Duración"
                field="VisitDetails.duration"
                filter
                filterField="created_at"
                filterPlaceholder="Buscar por duración"
                filterElement={
                  <NeoRangeNumberFilter
                    ref={tableRef}
                    field={"VisitDetails.duration"}
                    minPlaceholder={"Mín"}
                    maxPlaceholder={"Máx"}
                    minDigit={0}
                    maxDigit={0}
                  />
                }
                filterMatchMode="range"
                body={(row) => {
                  const { VisitDetails } = row;
                  const { duration } = VisitDetails || {};
                  
                  if(duration === undefined) return <span></span>;
                  if (duration === null) return <span>En curso</span>;

                  const hours = Math.floor(duration / 3600);
                  const minutes = Math.floor((duration % 3600) / 60);
                  const seconds = Math.floor(duration % 60);
                  const format = (part) => String(part).padStart(2, "0");
                  
                  return (
                    <span>{`${format(hours)}:${format(minutes)}:${format(seconds)}`}</span>
                  );
                }}
                sortable
              />
            }
             {/* ToDo: Agregar columnas de Calificación y Emoción de la encuesta enviada */}
          </NeoTable>
        </div>
      </div>
    </div>
  );
}
